import { useState } from 'react'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import { useQuoteContext } from '../../../hooks/useQuoteContext'
import useFeaturePermissions from '../../../hooks/useFeaturePermissions'
import InputGroup from 'react-bootstrap/InputGroup'
import toast, { Toast } from 'react-hot-toast'

export default function DifferenceQuote() {
   const state = useQuoteContext()
   const { hasPermission } = useFeaturePermissions()
   const { getString, isCoTerm, user_view } = state

   const [showEINWarning, setShowEINWarning] = useState<boolean>(false)
   const [showInfo, setShowInfo] = useState<boolean>(false)

   const isViewOnly = hasPermission(
      'DifferenceQuoteViewOnly',
      state.user_role,
      false,
   )
   const hasEditPermission = hasPermission('DifferenceQuote', state.user_role)
   const canSee = isViewOnly || hasEditPermission || state.differenceQuote
   // Users without normal permission can see this in readonly mode when differenceQuote is already true
   const isReadOnly =
      isViewOnly || (!hasEditPermission && state.differenceQuote)

   if (!canSee) return <></>

   const hasExistingEINs =
      (state.accountSnowflake?.EINs__c?.[0]?.length || 0) > 0

   const handleCheckboxChange = (): void => {
      if (isReadOnly) return

      if (isCoTerm && user_view !== 'External') {
         toast.custom(
            (t: Toast) => {
               const dismiss = () => toast.dismiss(t.id)
               const handler = (e: MouseEvent) => {
                  const el = document.getElementById(`toast-${t.id}`)
                  if (el && !el.contains(e.target as Node)) {
                     dismiss()
                     document.removeEventListener('mousedown', handler)
                  }
               }
               setTimeout(
                  () => document.addEventListener('mousedown', handler),
                  0,
               )
               return (
                  <div
                     id={`toast-${t.id}`}
                     style={{
                        background: '#fff',
                        border: '1px solid #e2e2e2',
                        borderRadius: '8px',
                        padding: '12px 16px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '10px',
                        maxWidth: '400px',
                        opacity: t.visible ? 1 : 0,
                        transition: 'opacity 0.2s',
                     }}
                  >
                     <span style={{ fontSize: '18px', lineHeight: '1.4' }}>
                        ⚠️
                     </span>
                     <span
                        style={{ flex: 1, fontSize: '14px', lineHeight: '1.4' }}
                     >
                        {getString(
                           'product.DifferenceQuote.CoTermWarning.Text',
                        )}
                     </span>
                     <button
                        onClick={dismiss}
                        style={{
                           background: 'none',
                           border: 'none',
                           cursor: 'pointer',
                           fontSize: '18px',
                           lineHeight: '1',
                           color: '#999',
                           padding: '0 0 0 4px',
                        }}
                        aria-label='Dismiss'
                     >
                        ✕
                     </button>
                  </div>
               )
            },
            { duration: 10000 },
         )
      }

      if (state.differenceQuote && hasExistingEINs) {
         setShowEINWarning(true)
      } else {
         if (
            state.differenceQuote &&
            state.accountSnowflake?.Expected_Total_MRR__c > 0
         ) {
            setShowInfo(true)
         }
         state.setDifferenceQuote((prev: boolean) => !prev)
      }
   }

   const handleInfoClose = (): void => {
      setShowInfo(false)
   }

   const handleEINWarningConfirm = (): void => {
      const existingEINCount = state.accountSnowflake?.EINs__c?.[0]?.length || 0
      state.setProductPayrollEINCount((prev: number) =>
         Math.max(0, prev - existingEINCount),
      )
      state.setProductPayrollEINNames((prev: string[]) =>
         prev.slice(Math.min(existingEINCount, prev.length)),
      )
      state.setProductPayrollEECounts((prev: number[]) =>
         prev.slice(Math.min(existingEINCount, prev.length)),
      )
      state.setProductPayrollStateCounts((prev: number[]) =>
         prev.slice(Math.min(existingEINCount, prev.length)),
      )
      state.setProductPayrollEINImpTypes((prev: any[]) =>
         prev.slice(Math.min(existingEINCount, prev.length)),
      )
      state.setProductEINIntegrations((prev: any[]) =>
         prev.slice(Math.min(existingEINCount, prev.length)),
      )
      state.setPayrollInfoRecords([])
      state.setDifferenceQuote(false)
      setShowEINWarning(false)
   }

   const handleEINWarningCancel = (): void => {
      setShowEINWarning(false)
   }

   return (
      <>
         <Modal
            backdrop='static'
            keyboard={true}
            show={showInfo}
            onHide={handleInfoClose}
            centered
         >
            <Modal.Header closeButton>
               <Modal.Title>
                  {getString('product.DifferenceQuote.Modal.Title')}
               </Modal.Title>
            </Modal.Header>
            <Modal.Body>
               <p>
                  {getString(
                     'product.DifferenceQuote.Modal.Body',
                     'Other than certain special circumstances like returning customers, and sometimes adding new EINs, this should usually be checked.',
                  )}
               </p>
            </Modal.Body>
            <Modal.Footer>
               <Button variant='primary' onClick={handleInfoClose}>
                  {getString('product.DifferenceQuote.Modal.Footer.Button')}
               </Button>
            </Modal.Footer>
         </Modal>

         <Modal
            backdrop='static'
            keyboard={true}
            show={showEINWarning}
            onHide={handleEINWarningCancel}
            centered
         >
            <Modal.Header closeButton>
               <Modal.Title>
                  {getString(
                     'product.DifferenceQuote.EINRemoval.Title',
                     'Are you Sure?',
                  )}
               </Modal.Title>
            </Modal.Header>
            <Modal.Body>
               <p>
                  {getString(
                     'product.DifferenceQuote.EINRemoval.WarningText',
                     'Other than certain special circumstances like returning customers, and sometimes adding new EINs, this should usually be checked. Unchecking the difference quote checkbox will remove all existing EIN data from this quote, and after saving this will become permanent. If you want a difference quote for this opportunity in the future, you will need to manually re-add the EIN information. If you change your mind before saving, simply refresh the page and the existing EINs will re-populate.',
                  )}
               </p>
            </Modal.Body>
            <Modal.Footer>
               <Button variant='secondary' onClick={handleEINWarningCancel}>
                  {getString('global.Cancel')}
               </Button>
               <Button variant='warning' onClick={handleEINWarningConfirm}>
                  {getString('global.Proceed')}
               </Button>
            </Modal.Footer>
         </Modal>

         <InputGroup className='justify-content-center' size='sm'>
            <InputGroup.Text>
               {getString('product.DifferenceQuote.InputText')}
            </InputGroup.Text>
            <InputGroup.Checkbox
               checked={state.differenceQuote}
               disabled={isReadOnly}
               onChange={handleCheckboxChange}
            />
         </InputGroup>
      </>
   )
}
